import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { convertToSecretCode, MonthlyLabels } from '../quarter-selector.constants';
import { QuarterSelectorState, QuarterSelectorType, QuarterSelectorValue } from '../quarter-selector.model';
import { Month, Quarter } from './quarter-selector-overlay.model';
import { QuarterSelectorStateService } from './quarter-selector.state';

@UntilDestroy()
@Component({
  styleUrls: ['quarter-selector-overlay.component.scss'],
  templateUrl: 'quarter-selector-overlay.component.html',
  standalone: false,
})
export class QuarterSelectorOverlayComponent implements OnInit, OnDestroy {
  public isMonthlySelector = false;
  public isYearlySelector = false;
  public selectedMonth = 0;
  public selectedQuarter = 0;
  public selectedYear: number | null = null;
  public selectorType: QuarterSelectorType;
  public requiredYear: boolean;
  public years: number[] = [];
  public minSelectableMonthValue = 0;
  public maxSelectableMonthValue = 0;
  public minSelectableQuarterValue = 0;
  public maxSelectableQuarterValue = 0;
  public label = '';
  public marketQtdValue: QuarterSelectorValue | Date;

  public thisSelection = 0;
  public quartersAndMonths: Quarter[] = [];

  private minValue: QuarterSelectorValue;
  private maxValue: QuarterSelectorValue;
  private allowedPeriods: Set<number>;
  private restrictedPeriods: Set<number>;
  private showAsQtd = false;

  constructor(private stateService: QuarterSelectorStateService) {}

  ngOnInit() {
    this.label = this.stateService.label;
    this.createQuartersAndMonths();
    this.processNewState();
    this.stateService.onStateChanged().pipe(untilDestroyed(this)).subscribe(this.processNewState.bind(this));
  }

  /**
   * This method is used by untilDestroyed. Do not delete it.
   */
  ngOnDestroy() {
    // used by untilDestroyed call.
  }

  private createQuartersAndMonths() {
    const state: QuarterSelectorState = this.stateService.getState();
    this.selectorType = state.selectorType;
    let quarterCount = 1;
    let currentQuarter = new Quarter(quarterCount);
    this.quartersAndMonths.push(currentQuarter);

    for (let i = 1; i <= 12; i++) {
      currentQuarter.months.push(new Month(MonthlyLabels.get(i), i, null, this.selectorType));
      const rest = i % 3;
      if (rest === 0 && quarterCount <= 3) {
        quarterCount++;
        currentQuarter = new Quarter(quarterCount);
        this.quartersAndMonths.push(currentQuarter);
      }
    }
  }

  private processNewState() {
    const state: QuarterSelectorState = this.stateService.getState();
    this.selectedYear = state.periodYear != null ? state.periodYear : 1;
    this.isMonthlySelector = state.selectorType === QuarterSelectorType.Monthly;
    this.isYearlySelector = state.selectorType === QuarterSelectorType.Yearly;
    this.selectorType = state.selectorType;
    this.requiredYear = state.requiredYear;
    this.minValue = state.minValue;
    this.maxValue = state.maxValue;
    this.showAsQtd = state.showAsQtd;
    this.allowedPeriods = state.allowedPeriods;
    this.restrictedPeriods = state.restrictedPeriods;
    this.marketQtdValue = state.marketQtdValue;

    if (this.isMonthlySelector) {
      this.selectedMonth = state.periodValue;
      this.selectMonth(new Month('', state.periodValue, this.selectedYear, this.selectorType));
    } else {
      const maxQuartersPerYear = 4;
      this.maxValue.periodValue =
        this.maxValue.periodValue > maxQuartersPerYear ? maxQuartersPerYear : this.maxValue.periodValue;
      this.minValue.periodValue =
        this.minValue.periodValue > maxQuartersPerYear ? maxQuartersPerYear : this.minValue.periodValue;
      this.selectedQuarter = state.periodValue;
      this.selectQuarter(new Quarter(state.periodValue, this.selectedYear));
    }
    this.fillYears();
    // if todays year is included in years and year is not set, current year is pre selected
    const todaysYear = new Date().getFullYear();
    if (this.isYearlySelector) {
      this.selectedYear = this.selectedYear != null ? this.selectedYear : null; // Set selectedYear as blank
    } else if (state.requiredYear && this.selectedYear <= 0) {
      if (this.years.includes(todaysYear)) {
        this.selectedYear = todaysYear;
      } else if (this.years.includes(this.maxValue.periodYear)) {
        this.selectedYear = this.maxValue.periodYear;
      }
    }
    this.composeMinAndMaxValues();
    this.setYearAndOptions();
  }

  private setYearAndOptions() {
    this.quartersAndMonths.forEach(x => {
      x.marketQtdValue = this.marketQtdValue;
      x.setOptions({
        showAsQtd: this.showAsQtd,
        minMonthValue: this.minSelectableMonthValue,
        maxMonthValue: this.maxSelectableMonthValue,
        minQuarterValue: this.minSelectableQuarterValue,
        maxQuarterValue: this.maxSelectableQuarterValue,
        allowedPeriods: this.allowedPeriods,
        restrictedPeriods: this.restrictedPeriods,
      });
      x.setYear(this.selectedYear);
    });
  }

  private composeMinAndMaxValues() {
    let minMonthToSelect;
    let maxMonthToSelect;
    let minQuarterToSelect;
    let maxQuarterToSelect;

    if (this.isMonthlySelector) {
      minMonthToSelect = this.minValue.periodValue;
      maxMonthToSelect = this.maxValue.periodValue;
      minQuarterToSelect = Number.parseInt(this.minValue.periodValue / 3 + '');
      maxQuarterToSelect = Number.parseInt(this.maxValue.periodValue / 3 + '');
    } else {
      minMonthToSelect = (this.minValue.periodValue - 1) * 3;
      maxMonthToSelect = this.maxValue.periodValue * 3;
      minQuarterToSelect = this.minValue.periodValue - 1;
      maxQuarterToSelect = this.maxValue.periodValue;
    }

    this.minSelectableMonthValue = convertToSecretCode(
      this.minValue.periodYear,
      minMonthToSelect,
      QuarterSelectorType.Monthly
    );
    this.maxSelectableMonthValue = convertToSecretCode(
      this.maxValue.periodYear,
      maxMonthToSelect,
      QuarterSelectorType.Monthly
    );
    this.minSelectableQuarterValue = convertToSecretCode(
      this.minValue.periodYear,
      minQuarterToSelect,
      QuarterSelectorType.Quarter
    );
    this.maxSelectableQuarterValue = convertToSecretCode(
      this.maxValue.periodYear,
      maxQuarterToSelect,
      QuarterSelectorType.Quarter
    );
  }

  clickOnMonth($event, month: Month) {
    if (this.isMonthlySelector) {
      this.selectMonth(month);
      $event.stopPropagation();
      this.setState();
    }
  }

  clickOnQuarter($event, quarter: Quarter) {
    if (
      !this.isMonthlySelector &&
      quarter.value <= this.maxSelectableQuarterValue &&
      quarter.value > this.minSelectableQuarterValue &&
      !quarter.disabled
    ) {
      this.selectQuarter(quarter);
      $event.stopPropagation();
      this.setState();
    }
  }

  otherYearSelected() {
    this.setYearAndOptions();
    if (this.isYearlySelector) {
      this.setState();
    }
  }

  private fillYears() {
    if (this.requiredYear) {
      this.years = Array.from(Array(this.maxValue.periodYear + 1).keys()).slice(this.minValue.periodYear);
    }
  }

  private setState() {
    let periodValue = this.selectedQuarter;
    if (this.isYearlySelector) {
      periodValue = 1;
    } else if (this.isMonthlySelector) {
      periodValue = this.selectedMonth;
    }

    this.stateService.setStateAndFinishSelection({
      minValue: this.minValue,
      maxValue: this.maxValue,
      selectorType: this.selectorType,
      periodValue,
      periodYear: this.selectedYear,
      requiredYear: this.requiredYear,
      showAsQtd: this.showAsQtd,
      allowedPeriods: this.allowedPeriods,
      restrictedPeriods: this.restrictedPeriods,
      marketQtdValue: this.marketQtdValue,
    });
  }

  private selectMonth(month: Month) {
    this.selectedMonth = month.monthNumber;
    this.selectedQuarter = 0;
    this.thisSelection = month.value;
  }

  private selectQuarter(quarter: Quarter) {
    this.selectedQuarter = quarter.quarterNumber;
    this.selectedMonth = 0;
    this.thisSelection = quarter.value;
  }
}
